import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Container,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  VStack,
  Text,
  Link,
  Alert,
  AlertIcon,
  InputGroup,
  InputRightElement,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
  HStack,
} from '@chakra-ui/react'
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import { authState } from '../atoms/auth'
import { confirmPasswordReset, findAccountByEmail, requestPasswordReset, signIn } from '../services/apiService'
import { useAuthState } from '../utils/authUtils'
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons'

const SignIn: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth, setAuth] = useRecoilState(authState)
  const { reloadUser } = useAuthState()
  const toast = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [findEmail, setFindEmail] = useState('')
  const [resetEmail, setResetEmail] = useState('')
  const [isSubmittingFind, setIsSubmittingFind] = useState(false)
  const [isSubmittingReset, setIsSubmittingReset] = useState(false)

  const {
    isOpen: isFindIdOpen,
    onOpen: onFindIdOpen,
    onClose: onFindIdClose
  } = useDisclosure()
  const {
    isOpen: isResetOpen,
    onOpen: onResetOpen,
    onClose: onResetClose
  } = useDisclosure()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const { data: res } = await signIn(account, password)
      if (res.statusCode === 200) {
        await reloadUser()
        navigate('/')
      } else {
        setError(res.message || t('로그인에 실패했습니다.'))
      }
    } catch (error: any) {
      setError(error.response?.data?.message || t('로그인에 실패했습니다.'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleFindIdSubmit = async () => {
    if (!findEmail) return
    setIsSubmittingFind(true)
    try {
      const { data: res } = await findAccountByEmail(findEmail)
      toast({
        title: t('아이디 찾기'),
        description: res.message || t('이메일로 아이디를 전송했습니다.'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
      setFindEmail('')
      onFindIdClose()
    } catch (err: any) {
      toast({
        title: t('아이디 찾기 실패'),
        description: err.response?.data?.message || t('아이디 찾기에 실패했습니다.'),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    } finally {
      setIsSubmittingFind(false)
    }
  }

  const handlePasswordResetSubmit = async () => {
    if (!resetEmail) return
    setIsSubmittingReset(true)
    try {
      const { data: res } = await requestPasswordReset(resetEmail)
      toast({
        title: t('비밀번호 찾기'),
        description: res.message || t('이메일을 확인해 주세요.'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
      setResetEmail('')
      onResetClose()
    } catch (err: any) {
      toast({
        title: t('비밀번호 찾기 실패'),
        description: err.response?.data?.message || t('비밀번호 찾기에 실패했습니다.'),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    } finally {
      setIsSubmittingReset(false)
    }
  }

  const handleConfirmResetToken = async (token: string) => {
    try {
      const { data: res } = await confirmPasswordReset(token)
      toast({
        title: t('비밀번호 재설정 완료'),
        description: res.message || t('임시 비밀번호가 활성화되었습니다.'),
        status: 'success',
        duration: 5000,
        isClosable: true
      })
    } catch (err: any) {
      toast({
        title: t('비밀번호 재설정 실패'),
        description: err.response?.data?.message || t('요청을 완료할 수 없습니다.'),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('resetToken')
      setSearchParams(newParams, { replace: true })
    }
  }

  useEffect(() => {
    const token = searchParams.get('resetToken')
    if (token) {
      handleConfirmResetToken(token)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <DefaultLayout>
      <Container maxW="md" py={20}>
        <VStack spacing={8}>
          <Heading size="xl" color="brand.600">
            {t('로그인')}
          </Heading>

          <Box w="100%" bg="white" p={8} borderRadius="lg" boxShadow="md">
            {error && (
              <Alert status="error" mb={4} borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <VStack spacing={6}>
                <FormControl isRequired>
                  <FormLabel>{t('아이디')}</FormLabel>
                  <Input
                    type="text"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    placeholder={t('아이디를 입력하세요')}
                    size="lg"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>{t('비밀번호')}</FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('비밀번호를 입력하세요')}
                      size="lg"
                    />
                    <InputRightElement h="full">
                      <IconButton
                        aria-label={showPassword ? t('비밀번호 숨기기') : t('비밀번호 보기')}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        variant="ghost"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  w="100%"
                  isLoading={isLoading}
                  loadingText={t('로그인 중...')}
                >
                  {t('로그인')}
                </Button>

                <HStack justify="space-between" w="100%">
                  <Button variant="link" size="sm" onClick={onFindIdOpen}>
                    {t('아이디 찾기')}
                  </Button>
                  <Button variant="link" size="sm" onClick={onResetOpen}>
                    {t('비밀번호 찾기')}
                  </Button>
                </HStack>

                <Text>
                  {t('계정이 없으신가요?')}{' '}
                  <Link as={RouterLink} to="/signUp" color="brand.500">
                    {t('회원가입')}
                  </Link>
                </Text>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>

      {/* 아이디 찾기 모달 */}
      <Modal isOpen={isFindIdOpen} onClose={onFindIdClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('아이디 찾기')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>{t('가입 시 이메일')}</FormLabel>
              <Input
                type="email"
                value={findEmail}
                onChange={(e) => setFindEmail(e.target.value)}
                placeholder={t('example@email.com')}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onFindIdClose}>
              {t('취소')}
            </Button>
            <Button colorScheme="brand" isLoading={isSubmittingFind} onClick={handleFindIdSubmit}>
              {t('이메일 전송')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 비밀번호 찾기 모달 */}
      <Modal isOpen={isResetOpen} onClose={onResetClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('비밀번호 찾기')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>{t('가입 시 이메일')}</FormLabel>
              <Input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder={t('example@email.com')}
              />
            </FormControl>
            <Text fontSize="sm" color="gray.500" mt={3}>
              {t('이메일로 임시 비밀번호와 활성화 링크를 전송해 드립니다.')}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onResetClose}>
              {t('취소')}
            </Button>
            <Button colorScheme="brand" isLoading={isSubmittingReset} onClick={handlePasswordResetSubmit}>
              {t('이메일 전송')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DefaultLayout>
  )
}

export default SignIn
